import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Copy, CheckCircle2, Image, FileText, Coins } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface NFTSuccessData {
  tokenId?: string;
  transactionHash: string;
  contractAddress: string;
  imageUrl: string;
  metadataUrl: string;
  name: string;
  description?: string;
  mintPrice: string;
  platformFee: string;
  chainId: number;
  explorerUrl: string;
}

interface NFTSuccessCardProps {
  data: NFTSuccessData;
  onClose?: () => void;
  onMintAnother?: () => void;
}

export function NFTSuccessCard({ data, onClose, onMintAnother }: NFTSuccessCardProps) {
  const { toast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const getExplorerName = (chainId: number) => {
    switch (chainId) {
      case 56: return 'BSCScan';
      case 97: return 'BSCScan Testnet';
      case 1: return 'Etherscan';
      case 11155111: return 'Sepolia Etherscan';
      default: return 'Block Explorer';
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="w-full animate-in slide-in-from-top duration-500">
      <Card className="w-full border-2 border-green-500/50 shadow-xl shadow-green-500/20">
        <CardHeader className="space-y-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/20">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                  NFT Minted Successfully!
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Your NFT has been created and is now on the blockchain
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* NFT Preview */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Image className="h-5 w-5" />
              NFT Preview
            </h3>
            <div className="relative rounded-lg overflow-hidden border border-border bg-card">
              <img 
                src={data.imageUrl} 
                alt={data.name}
                className="w-full h-64 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23333" width="400" height="400"/%3E%3Ctext fill="%23fff" font-size="20" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage Loading...%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <h4 className="text-xl font-bold text-white">{data.name}</h4>
                {data.description && (
                  <p className="text-sm text-gray-300 mt-1 line-clamp-2">{data.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Transaction Details
            </h3>
            <div className="space-y-2 bg-muted/50 rounded-lg p-4">
              {/* Transaction Hash */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Transaction Hash:</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                    {formatAddress(data.transactionHash)}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => copyToClipboard(data.transactionHash, 'Transaction hash')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => window.open(`${data.explorerUrl}/tx/${data.transactionHash}`, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Contract Address */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Contract Address:</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                    {formatAddress(data.contractAddress)}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => copyToClipboard(data.contractAddress, 'Contract address')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => window.open(`${data.explorerUrl}/address/${data.contractAddress}`, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Token ID */}
              {data.tokenId && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Token ID:</span>
                  <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                    #{data.tokenId}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* IPFS Links */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              IPFS Storage
            </h3>
            <div className="space-y-2 bg-muted/50 rounded-lg p-4">
              {/* Image URL */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Image URL:</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(data.imageUrl, '_blank')}
                    className="h-7 text-xs"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View Image
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => copyToClipboard(data.imageUrl, 'Image URL')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Metadata URL */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Metadata URL:</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(data.metadataUrl, '_blank')}
                    className="h-7 text-xs"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View Metadata
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => copyToClipboard(data.metadataUrl, 'Metadata URL')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Cost Breakdown
            </h3>
            <div className="space-y-2 bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mint Price:</span>
                <span className="text-sm font-mono">{data.mintPrice}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Platform Fee (2.5%):</span>
                <span className="text-sm font-mono">{data.platformFee}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm font-semibold">Total Paid:</span>
                <span className="text-sm font-mono font-semibold">{data.mintPrice}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => window.open(`${data.explorerUrl}/tx/${data.transactionHash}`, '_blank')}
              className="w-full"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View on {getExplorerName(data.chainId)}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`${data.explorerUrl}/token/${data.contractAddress}${data.tokenId ? `?a=${data.tokenId}` : ''}`, '_blank')}
              className="w-full"
            >
              <Image className="h-4 w-4 mr-2" />
              View NFT
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            {onMintAnother && (
              <Button
                onClick={onMintAnother}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                Mint Another NFT
              </Button>
            )}
            {onClose && (
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
